import React from 'react';
import { Box, Button, Typography } from '@mui/material';

import { LiveEvent } from '@/types';
import { TennisScoreDisplay } from './TennisScoreDisplay';

interface LiveEventCardTeamRowProps {
  event: LiveEvent;
  teamIndex: 1 | 2;
  onTeamClick: (event: LiveEvent, index: 1 | 2) => void;
  moneylineColor: string;
  hideButton?: boolean;
}

export function LiveEventCardTeamRow({
  event,
  teamIndex,
  onTeamClick,
  moneylineColor,
  hideButton = false,
}: LiveEventCardTeamRowProps) {
  const team = teamIndex === 1 ? event.team1 : event.team2;
  const player = teamIndex === 1 ? event.player1 : event.player2;
  const price = teamIndex === 1 ? event.price1 : event.price2;
  const isPlayer1 = teamIndex === 1;

  const isTennis = (event.league === 'ATP' || event.league === 'WTA') && !!player?.scores;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexDirection: { xs: 'row', md: 'row' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        {team?.abbr ? (
          <Box
            sx={{
              bgcolor: team.color || 'action.disabled',
              color: 'common.white',
              px: 0.75,
              py: 0.5,
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: 700,
              minWidth: 40,
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            {team.abbr}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '1.25rem', flexShrink: 0 }}>
            {player?.flag || team?.logo}
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {player?.name || team?.name}
        </Typography>
        {team?.record && !isTennis && (
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: 'text.secondary',
              fontWeight: 400,
              flexShrink: 0,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {team.record}
          </Typography>
        )}
      </Box>
      {(player?.scores || typeof team?.score === 'number') && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            ml: 1,
            flexShrink: 0,
          }}
        >
          {isTennis ? (
            <TennisScoreDisplay scores={player?.scores || ''} isPlayer1={isPlayer1} />
          ) : (
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 400, color: 'text.primary' }}>
              {player?.scores || team?.score}
            </Typography>
          )}
        </Box>
      )}
      {/* Desktop: Show button on the right (unless hidden) */}
      {!hideButton && (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onTeamClick(event, teamIndex);
          }}
          variant="contained"
          sx={{
            display: { xs: 'none', md: 'flex' },
            bgcolor: moneylineColor,
            color: 'common.white',
            fontWeight: 700,
            fontSize: '0.875rem',
            py: 0.75,
            px: 3,
            minWidth: 100,
            borderRadius: '8px',
            textTransform: 'none',
            boxShadow: (theme) =>
              `0 5px 0 ${isPlayer1 ? theme.palette.primary.dark : theme.palette.secondary.dark}`,
            ml: 1,
            flexShrink: 0,
            '&:hover': {
              bgcolor: isPlayer1 ? 'primary.dark' : 'secondary.dark',
              boxShadow: (theme) =>
                `0 4px 0 ${isPlayer1 ? theme.palette.primary.dark : theme.palette.secondary.dark}`,
            },
          }}
        >
          {price}
        </Button>
      )}
    </Box>
  );
}
